/* Some mappings for cargo units defined in database (public.cargo_units) */

import { CUBIC_METER_IN_CUBIC_FEET } from './VolumeUnits';
import { PaymentUnit } from '@seaber/common/graphql/types';
export const LONG_TON_IN_METRIC_TONS = 1.016047;
export const SHORT_TON_IN_METRIC_TONS = 0.907185;
export const POUND_IN_METRIC_TONS = 0.00045359237;

const massCargoUnits = ['t', 'admt', 'mts', 'bdmt', 'lt', 'kg', 'st'] as const;
const volumeCargoUnits = ['m3', 'ft3', 'cbm', 'scbm ub', 'scbm ob'] as const;

export const reducedUnits = ['mts', 'm3', 'ft3', 'pcs', 'set'];

export type PhysicalQuantity = 'mass' | 'volume';

export type MetricTonnes = number & { __volumeUnitBrand: 'mts' };
export type CubicMeters = number & { __volumeUnitBrand: 'm3' };

/** Returns which physical quantity the cargo unit represents */
export function getCargoUnitQuantity(
  cargoUnitName: string,
): PhysicalQuantity | null {
  if (
    massCargoUnits.includes(cargoUnitName as (typeof massCargoUnits)[number])
  ) {
    return 'mass';
  }

  if (
    volumeCargoUnits.includes(
      cargoUnitName as (typeof volumeCargoUnits)[number],
    )
  ) {
    return 'volume';
  }

  return null;
}

/** Returns cargo amount in mt/m3, very naive implementation */
export function cargoAmountToSystemUnit(
  amount: number,
  cargoUnitName: string,
): number {
  switch (cargoUnitName) {
    case 'mts':
    case 'admt':
    case 'bdmt':
    case 't': {
      return amount;
    }
    case 'kg': {
      return amount / 1000;
    }
    case 'scbm ub':
    case 'scbm ob':
    case 'lcbm':
    case 'cbm':
    case 'm3': {
      return amount;
    }
    case 'ft3': {
      return amount / CUBIC_METER_IN_CUBIC_FEET;
    }
    case 'lt': {
      return amount * LONG_TON_IN_METRIC_TONS;
    }
    case 'st': {
      return amount * SHORT_TON_IN_METRIC_TONS;
    }
    case 'set':
    case 'pcs': {
      return amount;
    }
    default: {
      console.error(
        'cargoAmountToSystemUnit: No correct mapping found for cargo unit',
        cargoUnitName,
      );
      return amount;
    }
  }
}

/** Returns system cargo amount in given cargo units */
export function systemAmountToCargoUnit(
  amount: number,
  cargoUnitName: string,
): number {
  return amount / cargoAmountToSystemUnit(1, cargoUnitName);
}

/** Maps a cargo unit name to its corresponding payment unit */
export function cargoUnitToPaymentUnit(
  cargoUnit: string | null | undefined,
): PaymentUnit {
  if (!cargoUnit) {
    return PaymentUnit.MT;
  }

  const unit = cargoUnit.toLowerCase();

  switch (unit) {
    case 'bdmt':
      return PaymentUnit.BDMT;
    case 't':
    case 'mts':
    case 'admt':
    case 'kg':
    case 'lt':
    case 'st':
      return PaymentUnit.MT;
    case 'm3':
    case 'cbm':
      return PaymentUnit.M3;
    case 'ft3':
      return PaymentUnit.FT3;
    case 'scbm ub':
      return PaymentUnit.SCBM_UB;
    case 'pcs':
      return PaymentUnit.PER_PCS;
    case 'set':
      return PaymentUnit.PER_SET;
    default: {
      const quantity = getCargoUnitQuantity(unit);
      return quantity === 'volume'
        ? PaymentUnit.M3
        : quantity === 'mass'
          ? PaymentUnit.MT
          : unit === 'set'
            ? PaymentUnit.PER_SET
            : PaymentUnit.PER_PCS;
    }
  }
}
